/* eslint-disable max-lines */
import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { secondsToWeeks } from '@modernloop/shared/datetime';
import { logError } from '@modernloop/shared/utils';

import {
  SourcingLinkTaskAction,
  TaskCreateValidateErrorFlagType,
  TaskCreationSource,
  useJobStageDefaultTaskQuery,
  useQuickTaskJobStageSettingsQuery,
} from 'src/generated/mloop-graphql';

import getJobDetailsUrl, { JobDetailsTabs } from 'src/urls/getJobDetailsUrl';
import {
  JobStageSubMenuUrlType,
  JobStageTab,
  LinkTargets,
  getJobStageSubMenuUrl,
} from 'src/urls/getJobStageSubMenuUrl';
import {
  OrganizationSettingsTab,
  OrganizationSettingsUrlType,
  getOrganizationSettingUrls,
} from 'src/urls/getOrganizationSettingUrls';

export const MAXIMUM_SUGGESTION_COUNT = 100;
export const MINIMUM_WARNING_SUGGESTION_COUNT = 10;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QuickTaskJobStageSettings = gql`
  query QuickTaskJobStageSettings($jobStageId: uuid!) {
    jobStage(id: $jobStageId) {
      id
      jobStageSettingsV2 {
        id
        candidateAvailabilitySettingsV2 {
          timeframe {
            duration {
              seconds
            }
          }
        }
        selfScheduleSettingsV2 {
          timeframe {
            duration {
              seconds
            }
          }
        }
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const JobStageDefaultTaskQuery = gql`
  query JobStageDefaultTask($id: uuid!) {
    jobStage(id: $id) {
      id
      jobStageSettingsV2 {
        id
        defaultTask
        isAutomatedTaskEnabled
      }
    }
  }
`;

export const useGetDefaultTaskType = (
  jobStageId: string
): {
  defaultTask: TaskCreationSource | undefined;
  isAutomatedTaskEnabled: boolean;
  result: ReturnType<typeof useJobStageDefaultTaskQuery>;
} => {
  const result = useJobStageDefaultTaskQuery({
    variables: {
      id: jobStageId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !jobStageId,
  });

  return {
    defaultTask: result?.data?.jobStage?.jobStageSettingsV2?.defaultTask || undefined,
    isAutomatedTaskEnabled:
      !!result?.data?.jobStage?.jobStageSettingsV2?.defaultTask &&
      !!result?.data?.jobStage?.jobStageSettingsV2?.isAutomatedTaskEnabled,
    result,
  };
};

export const getErrorInfoNavigation = ({
  type,
  jobId,
  jobStageId,
}: {
  type: TaskCreateValidateErrorFlagType;
  jobId: string;
  jobStageId: string;
}): OrganizationSettingsUrlType | JobStageSubMenuUrlType | null => {
  switch (type) {
    /** Interview plan navigation start */
    case TaskCreateValidateErrorFlagType.InterviewPlanWithZeroInterviews:
    case TaskCreateValidateErrorFlagType.InterviewPlanIncomplete:
    case TaskCreateValidateErrorFlagType.InterviewPlanSeatWithZeroInterviewers:
    case TaskCreateValidateErrorFlagType.InterviewPlanWithMultiDay:
    case TaskCreateValidateErrorFlagType.InterviewPlanSeatWithZeroEligibleInterviewers:
    case TaskCreateValidateErrorFlagType.InterviewPlanHasHiddenInterviews:
    case TaskCreateValidateErrorFlagType.InterviewPlanHasDynamicBreaks:
    case TaskCreateValidateErrorFlagType.InterviewerDoesNotHaveZoomAccount:
    case TaskCreateValidateErrorFlagType.SchedulerDoesNotHaveZoomAccount:
    case TaskCreateValidateErrorFlagType.NoSuggestionsFoundForSelfScheduleTask:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.INTERVIEW_PLAN,
        extraParams: { linkTarget: LinkTargets.interviewPlanSection },
      });
    /** Interview plan navigation end */

    /** Schedule location plan navigation start */
    case TaskCreateValidateErrorFlagType.MissingInterviewMeetingLocation:
    case TaskCreateValidateErrorFlagType.ScheduleLocationCustomLinkSetButNotDefined:
    case TaskCreateValidateErrorFlagType.ScheduleLocationCustomTextSetButNotDefined:
    case TaskCreateValidateErrorFlagType.ScheduleLocationCustomPhoneSetButNotDefined:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.INTERVIEW_PLAN,
        extraParams: { linkTarget: LinkTargets.scheduleLocationSection },
      });
    /** Schedule location plan navigation end */

    /** Requests tab navigation start */
    case TaskCreateValidateErrorFlagType.TemplateNotSetAvailabilityRequestEmail:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.REQUESTS,
        extraParams: { linkTarget: LinkTargets.availabilityJobStagRequestSection },
      });
    case TaskCreateValidateErrorFlagType.TemplateNotSetSelfScheduleRequestEmail:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.REQUESTS,
        extraParams: { linkTarget: LinkTargets.selfScheduleJobStageRequestSection },
      });
    /** Requests tab navigation end */

    /** COMMUNICATIONS tab navigation start */
    case TaskCreateValidateErrorFlagType.TemplateNotSetCandidateConfirmationEmail:
    case TaskCreateValidateErrorFlagType.TemplateNotSetCandidateCalendarInvite:
    case TaskCreateValidateErrorFlagType.YouHaveInsufficientPermissionOnCandidateCalendar:
    case TaskCreateValidateErrorFlagType.BackupSenderHasInsufficientPermissionOnCandidateCalendar:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.COMMUNICATIONS,
        extraParams: { linkTarget: LinkTargets.candidateCommunicationJobStageSection },
      });

    case TaskCreateValidateErrorFlagType.TemplateNotSetInternalCalendarInvite:
    case TaskCreateValidateErrorFlagType.YouHaveInsufficientPermissionOnInternalCalendar:
    case TaskCreateValidateErrorFlagType.BackupSenderHasInsufficientPermissionOnInternalCalendar:
      return getJobStageSubMenuUrl({
        jobId,
        jobStageId,
        tab: JobStageTab.COMMUNICATIONS,
        extraParams: { linkTarget: LinkTargets.internalCommunicationJobStageSection },
      });
    /** COMMUNICATIONS tab navigation end */

    /** Org settings  tab navigation start */
    case TaskCreateValidateErrorFlagType.BackupSenderIsIneligible:
      return getOrganizationSettingUrls({ tab: OrganizationSettingsTab.SCHEDULE_SETTINGS });
    case TaskCreateValidateErrorFlagType.MissingZoomIntegration:
      return getOrganizationSettingUrls({ tab: OrganizationSettingsTab.INTEGRATIONS });
    /** Org settings  tab navigation end */

    default:
      logError(`Unknown TaskCreateValidateErrorFlagType  ${type} in getErrorInfoNavigation`);
      return null;
  }
};

export const getSourcingLinkErrorInfoNavigation = ({
  type,
  jobId,
  jobStageId,
}: {
  type: TaskCreateValidateErrorFlagType;
  jobId: string;
  jobStageId: string;
}) => {
  switch (type) {
    /** Sourcing link Interview plan navigation start */
    case TaskCreateValidateErrorFlagType.InterviewPlanWithZeroInterviews:
    case TaskCreateValidateErrorFlagType.InterviewPlanIncomplete:
    case TaskCreateValidateErrorFlagType.InterviewPlanSeatWithZeroInterviewers:
    case TaskCreateValidateErrorFlagType.InterviewPlanWithMultiDay:
    case TaskCreateValidateErrorFlagType.InterviewPlanSeatWithZeroEligibleInterviewers:
    case TaskCreateValidateErrorFlagType.InterviewPlanHasHiddenInterviews:
    case TaskCreateValidateErrorFlagType.InterviewPlanHasDynamicBreaks:
    case TaskCreateValidateErrorFlagType.InterviewerDoesNotHaveZoomAccount:
    case TaskCreateValidateErrorFlagType.SchedulerDoesNotHaveZoomAccount:
    case TaskCreateValidateErrorFlagType.NoSuggestionsFoundForSelfScheduleTask:
      return getJobDetailsUrl(jobId, JobDetailsTabs.Sourcing, undefined, {
        linkTarget: LinkTargets.interviewPlanSection,
      });
    /** Sourcing Interview plan navigation end */
    case TaskCreateValidateErrorFlagType.SourcingLinkJobStageNotFound:
      return getJobDetailsUrl(jobId, JobDetailsTabs.Sourcing, undefined, {
        linkTarget: LinkTargets.jobStageSelectSection,
      });

    default:
      return getErrorInfoNavigation({ type, jobId, jobStageId });
  }
};

export const isAssigneAutomateTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskAutomateAssignTask;
};

export const isAvailabilityAutomateTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskAutomateAvailability;
};
export const isSelfScheduleAutomateTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskAutomateSelfSchedule;
};

/**
 * Check for automation task type only i.e TaskAutomate
 */
export const isAutomateTask = (taskType: TaskCreationSource) => {
  return (
    isAssigneAutomateTask(taskType) || isAvailabilityAutomateTask(taskType) || isSelfScheduleAutomateTask(taskType)
  );
};

const isAssigneOneClickTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskQuickCreateAssignTask;
};

const isAvailabilitOneClickTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskQuickCreateAvailability;
};
const isSelfScheduleOneClickTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.TaskQuickCreateSelfSchedule;
};

/**
 * Check for one click task type only i.e Quick task type
 */
const isOneClickTask = (taskType: TaskCreationSource) => {
  return isAssigneOneClickTask(taskType) || isAvailabilitOneClickTask(taskType) || isSelfScheduleOneClickTask(taskType);
};

export const isAssigneQuickTask = (taskType: TaskCreationSource) => {
  return isAssigneOneClickTask(taskType) || isAssigneAutomateTask(taskType);
};

export const isAvailabilityQuickTask = (taskType: TaskCreationSource) => {
  return isAvailabilitOneClickTask(taskType) || isAvailabilityAutomateTask(taskType);
};
export const isSelfScheduleQuickTask = (taskType: TaskCreationSource) => {
  return isSelfScheduleOneClickTask(taskType) || isSelfScheduleAutomateTask(taskType);
};

export const isSourcingLinkAvailabilityQuickTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.SourcingLinkAvailability;
};
export const isSourcingLinkSelfScheduleQuickTask = (taskType: TaskCreationSource) => {
  return taskType === TaskCreationSource.SourcingLinkSelfSchedule;
};

/**
 * Check for quick task & automation
 */
export const isQuickTask = (taskType?: TaskCreationSource) => {
  if (!taskType) {
    return false;
  }
  return isOneClickTask(taskType) || isAutomateTask(taskType);
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const getQuickTaskName = (t: Translate, taskType?: TaskCreationSource): string => {
  if (!taskType || !isQuickTask(taskType)) {
    return t('hiring-dashboard-common:common.quickTask.noDefaultTask');
  }

  if (isAvailabilityQuickTask(taskType)) {
    return t('hiring-dashboard-common:common.quickTask.availabilityRequestName');
  }

  if (isSelfScheduleQuickTask(taskType)) {
    return t('hiring-dashboard-common:common.quickTask.selfScheduleRequestName');
  }
  return t('hiring-dashboard-common:common.quickTask.teamMemberRequestName');
};

export const getQuickTaskTypeForSourcingLinkTaskActionType = (type: SourcingLinkTaskAction): TaskCreationSource => {
  switch (type) {
    case SourcingLinkTaskAction.Availability:
      return TaskCreationSource.SourcingLinkAvailability;
    case SourcingLinkTaskAction.SelfSchedule:
      return TaskCreationSource.SourcingLinkSelfSchedule;
    default:
      throw new Error(`Unknown SourcingLinkTaskAction type ${type}`);
  }
};

export const useGetCanCreateQuickSubTitle = ({
  taskType,
  jobStageId,
  hasWarnings,
  suggestionCount,
}: {
  taskType: TaskCreationSource;
  jobStageId: string;
  hasWarnings: boolean;
  suggestionCount: number;
}): string => {
  const { t } = useTranslation();
  /**
   * Currently subtitle is only shown of Availability & SelfSchedule
   */
  const isQuickTaskType = isAvailabilityQuickTask(taskType) || isSelfScheduleQuickTask(taskType);

  const { data } = useQuickTaskJobStageSettingsQuery({
    variables: {
      jobStageId,
    },
    skip: !isQuickTaskType,
  });

  if (!isQuickTaskType || suggestionCount < MINIMUM_WARNING_SUGGESTION_COUNT) {
    return '';
  }

  let durationObj;

  if (isAvailabilityQuickTask(taskType)) {
    durationObj = data?.jobStage?.jobStageSettingsV2?.candidateAvailabilitySettingsV2;
  } else if (isSelfScheduleQuickTask(taskType)) {
    durationObj = data?.jobStage?.jobStageSettingsV2?.selfScheduleSettingsV2;
  }

  const duration = durationObj?.timeframe?.duration?.seconds || 0;
  const week = secondsToWeeks(duration) || 2;

  if (hasWarnings) {
    return t('hiring-dashboard-common:common.quickTask.availabilityWarningSubtitle', { count: week });
  }

  return t('hiring-dashboard-common:common.quickTask.availabilitySuccessSubtitle', { count: week });
};

export const getMappedTaskCreationTypeForAutomationType = (defaultTaskType: TaskCreationSource) => {
  switch (defaultTaskType) {
    case TaskCreationSource.TaskAutomateAvailability:
      return TaskCreationSource.TaskQuickCreateAvailability;
    case TaskCreationSource.TaskAutomateSelfSchedule:
      return TaskCreationSource.TaskQuickCreateSelfSchedule;
    case TaskCreationSource.TaskAutomateAssignTask:
      return TaskCreationSource.TaskQuickCreateAssignTask;
    default:
      return defaultTaskType;
  }
};
